import type { ReactNode } from 'react';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';

import { Radio, RadioGroup } from '@/components/common/radio';

import { FormField } from '../form-field';

import styles from './radio-group-field.module.scss';

export type RadioOption = {
  label: ReactNode;
  value: string | number;
  disabled?: boolean;
};

export type RadioGroupFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = {
  name: TName;
  control: Control<TFieldValues>;
  options: RadioOption[];
  label?: string;
  help?: string;
  required?: boolean;
  direction?: 'vertical' | 'horizontal';
  disabled?: boolean;
};

export function RadioGroupField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  name,
  control,
  options,
  label,
  help,
  required,
  direction = 'vertical',
  disabled,
}: RadioGroupFieldProps<TFieldValues, TName>) {
  return (
    <FormField name={name} control={control} label={label} help={help} required={required}>
      {({ field }) => (
        <RadioGroup
          value={field.value}
          onChange={(event) => field.onChange(event.target.value)}
          onBlur={field.onBlur}
          disabled={disabled}
          className={direction === 'horizontal' ? styles.horizontal : styles.vertical}
        >
          {options.map((option) => (
            <Radio key={String(option.value)} value={option.value} disabled={option.disabled}>
              {option.label}
            </Radio>
          ))}
        </RadioGroup>
      )}
    </FormField>
  );
}
